import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SEO from "../../components/SEO/SEO";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import {
  FaEye,
  FaEyeSlash,
  FaSignInAlt,
  FaUserPlus,
  FaArrowLeft,
  FaCheck,
  FaTimes,
} from "react-icons/fa";
import { login, register } from "../../store/slices/auth/asyncThunks";
import {
  selectLoginPending,
  selectRegisterPending,
  selectError,
  selectLoggedIn,
  selectUser,
  clearError,
} from "../../store/slices/auth/slice";
import {
  Container,
  LogoImage,
  CardContainer,
  Card,
  CardFace,
  CardFront,
  CardBack,
  FormHeader,
  FormTitle,
  FormDescription,
  Form,
  FormGroup,
  Label,
  Input,
  PasswordGroup,
  PasswordToggle,
  SubmitButton,
  SwitchForm,
  SwitchText,
  SwitchButton,
  Message,
  ForgotPassword,
  BackButton,
  PasswordRequirements,
  PasswordRequirement,
} from "./AuthPage.styles";
import LogoImg from "../../assets/images/logo-01.png";

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const [isFlipped, setIsFlipped] = useState(false);
  const [showPassword, setShowPassword] = useState({
    login: false,
    register: false,
    confirm: false,
  });
  const [message, setMessage] = useState({ text: "", type: "" });
  const [formData, setFormData] = useState({
    login: { email: "", password: "" },
    register: {
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  // Redux selectors
  const isLoginLoading = useSelector(selectLoginPending);
  const isRegisterLoading = useSelector(selectRegisterPending);
  const error = useSelector(selectError);
  const isLoggedIn = useSelector(selectLoggedIn);
  const user = useSelector(selectUser);

  const password = formData.register.password;
  const passwordChecks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /\d/.test(password),
  };

  const flipCard = () => {
    setIsFlipped(!isFlipped);
    setMessage({ text: "", type: "" });
    dispatch(clearError());
  };

  const togglePassword = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Dozvoli samo interne putanje kao redirect — zaštita od open redirect napada
  // (npr. /pristup?redirect=https://zlonameran-sajt.com)
  const getSafeRedirectUrl = useCallback(() => {
    const redirectUrl = searchParams.get("redirect");
    if (
      !redirectUrl ||
      !redirectUrl.startsWith("/") ||
      redirectUrl.startsWith("//") ||
      redirectUrl.includes("\\")
    ) {
      return null;
    }
    try {
      const resolved = new URL(redirectUrl, window.location.origin);
      if (resolved.origin !== window.location.origin) {
        return null;
      }
    } catch {
      return null;
    }
    return redirectUrl;
  }, [searchParams]);

  const handleInputChange = (form, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [form]: {
        ...prev[form],
        [field]: value,
      },
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    try {
      const { email, password } = formData.login;

      await dispatch(login({ email, password })).unwrap();

      // toast.success("Uspešna prijava! Preusmeravamo vas...");
    } catch (error) {
      // Error will be handled by Redux and displayed via useEffect

      toast.error(error.message || "Pogrešna email adresa ili lozinka.");
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    try {
      const { email, password, confirmPassword } = formData.register;

      if (password !== confirmPassword) {
        toast.error("Lozinke se ne slažu!");
        return;
      }

      if (!passwordChecks.length || !passwordChecks.lowercase || !passwordChecks.uppercase || !passwordChecks.number) {
        toast.error("Lozinka mora imati najmanje 8 karaktera, veliko slovo, malo slovo i broj!");
        return;
      }

      const result = await dispatch(
        register({ email, password, confirmPassword })
      ).unwrap();

      // Use message from backend
      toast.success(
        result.message ||
          "Nalog je uspešno kreiran! Proverite email za potvrdu."
      );

      // needsEmailVerification iz selektora je u ovom closure-u uvek zastarelo
      // (Redux se ažurira tek posle submit-a), pa odluku donosimo iz odgovora servera
      const shouldVerifyEmail = !result.user?.isEmailVerified;

      // If email verification is needed, redirect to verification page
      setTimeout(() => {
        if (shouldVerifyEmail) {
          navigate(`/verifikacija?email=${encodeURIComponent(email)}`);
        } else {
          // Check for redirect parameter or use role-based routing
          const redirectUrl = getSafeRedirectUrl();
          if (redirectUrl) {
            navigate(redirectUrl);
          } else if (result.user?.role === "admin") {
            navigate("/admin-panel");
          } else {
            navigate("/korisnicki-panel/kursevi");
          }
        }
      }, 2000);
    } catch (error) {
      toast.error(error.message || "Greška prilikom registracije. Pokušajte ponovo.");
    }
  };

  const forgotPassword = () => {
    navigate("/resetovanje-lozinke");
  };

  const goHome = () => {
    navigate("/");
  };

  useEffect(() => {
    if (isLoggedIn && user) {
      // Check for redirect parameter or use role-based routing
      const redirectUrl = getSafeRedirectUrl();
      if (redirectUrl) {
        navigate(redirectUrl);
      } else if (user.role === "admin") {
        navigate("/admin-panel");
      } else {
        navigate("/korisnicki-panel/kursevi");
      }
    }
  }, [isLoggedIn, user, navigate, getSafeRedirectUrl]);

  // Show Redux error messages
  useEffect(() => {
    if (error) {
      // toast.error(error);
    }
  }, [error]);

  return (
    <Container>
      <SEO
        title="Prijava i registracija"
        description="Prijavite se ili napravite nalog za pristup online edukacijama MITOVKA BeautySpot."
        path="/pristup"
      />

      <BackButton onClick={goHome}>
        <FaArrowLeft />
        Nazad na sajt
      </BackButton>

      <LogoImage src={LogoImg} alt="MITOVKA BEAUTYSPOT Logo" />

      <CardContainer $isFlipped={!isFlipped}>
        <Card $isFlipped={isFlipped}>
          {/* Login Side */}
          <CardFace as={CardFront}>
            <FormHeader>
              <FormTitle>Prijava</FormTitle>
              <FormDescription>
                Dobrodošli nazad! Prijavite se na vaš nalog da nastavite učenje.
              </FormDescription>
            </FormHeader>

            {message.text && (
              <Message $type={message.type}>{message.text}</Message>
            )}

            <Form onSubmit={handleLogin}>
              <FormGroup>
                <Label>Email Adresa</Label>
                <Input
                  type="email"
                  placeholder="vaša@email.com"
                  value={formData.login.email}
                  onChange={(e) =>
                    handleInputChange("login", "email", e.target.value)
                  }
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Lozinka</Label>
                <PasswordGroup>
                  <Input
                    type={showPassword.login ? "text" : "password"}
                    placeholder="Unesite lozinku"
                    value={formData.login.password}
                    onChange={(e) =>
                      handleInputChange("login", "password", e.target.value)
                    }
                    required
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => togglePassword("login")}
                  >
                    {showPassword.login ? <FaEyeSlash /> : <FaEye />}
                  </PasswordToggle>
                </PasswordGroup>
              </FormGroup>

              <SubmitButton
                type="submit"
                disabled={isLoginLoading}
                $isLoading={isLoginLoading}
              >
                {isLoginLoading && (
                  <l-line-spinner
                    size="20"
                    stroke="1.5"
                    speed="1"
                    color="#fff"
                  />
                )}
                <FaSignInAlt />
                Prijavite Se
              </SubmitButton>

              <ForgotPassword onClick={forgotPassword}>
                Zaboravili ste lozinku?
              </ForgotPassword>
            </Form>

            <SwitchForm>
              <SwitchText>Nemate nalog?</SwitchText>
              <SwitchButton onClick={flipCard}>
                <FaUserPlus />
                Registrujte Se
              </SwitchButton>
            </SwitchForm>
          </CardFace>

          {/* Register Side */}
          <CardFace as={CardBack}>
            <FormHeader>
              <FormTitle>Registracija</FormTitle>
              <FormDescription>
                Kreirajte nalog i započnite svoje putovanje u svetu beauty
                artista.
              </FormDescription>
            </FormHeader>

            {message.text && (
              <Message $type={message.type}>{message.text}</Message>
            )}

            <Form onSubmit={handleRegister}>
              <FormGroup>
                <Label>Email Adresa</Label>
                <Input
                  type="email"
                  placeholder="vaša@email.com"
                  value={formData.register.email}
                  onChange={(e) =>
                    handleInputChange("register", "email", e.target.value)
                  }
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Lozinka</Label>
                <PasswordGroup>
                  <Input
                    type={showPassword.register ? "text" : "password"}
                    placeholder="Minimum 8 karaktera"
                    value={formData.register.password}
                    onChange={(e) =>
                      handleInputChange("register", "password", e.target.value)
                    }
                    required
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => togglePassword("register")}
                  >
                    {showPassword.register ? <FaEyeSlash /> : <FaEye />}
                  </PasswordToggle>
                </PasswordGroup>
                {password.length > 0 && (
                  <PasswordRequirements>
                    <PasswordRequirement $met={passwordChecks.length}>
                      {passwordChecks.length ? <FaCheck /> : <FaTimes />} Min. 8 karaktera
                    </PasswordRequirement>
                    <PasswordRequirement $met={passwordChecks.uppercase}>
                      {passwordChecks.uppercase ? <FaCheck /> : <FaTimes />} Veliko slovo
                    </PasswordRequirement>
                    <PasswordRequirement $met={passwordChecks.lowercase}>
                      {passwordChecks.lowercase ? <FaCheck /> : <FaTimes />} Malo slovo
                    </PasswordRequirement>
                    <PasswordRequirement $met={passwordChecks.number}>
                      {passwordChecks.number ? <FaCheck /> : <FaTimes />} Broj
                    </PasswordRequirement>
                  </PasswordRequirements>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Potvrdite Lozinku</Label>
                <PasswordGroup>
                  <Input
                    type={showPassword.confirm ? "text" : "password"}
                    placeholder="Ponovo unesite lozinku"
                    value={formData.register.confirmPassword}
                    onChange={(e) =>
                      handleInputChange(
                        "register",
                        "confirmPassword",
                        e.target.value
                      )
                    }
                    required
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => togglePassword("confirm")}
                  >
                    {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
                  </PasswordToggle>
                </PasswordGroup>
              </FormGroup>

              <SubmitButton
                type="submit"
                disabled={isRegisterLoading}
                $isLoading={isRegisterLoading}
              >
                {isRegisterLoading && (
                  <l-line-spinner
                    size="20"
                    stroke="1.5"
                    speed="1"
                    color="#fff"
                  />
                )}
                <FaUserPlus />
                Kreiraj Nalog
              </SubmitButton>
            </Form>

            <SwitchForm>
              <SwitchText>Već imate nalog?</SwitchText>
              <SwitchButton onClick={flipCard}>
                <FaSignInAlt />
                Prijavite Se
              </SwitchButton>
            </SwitchForm>
          </CardFace>
        </Card>
      </CardContainer>
    </Container>
  );
};

export default AuthPage;
